/**
 * What came in, and what will happen if you press Importeren.
 *
 * The review screen already let you check the order before anything was
 * created. The folder it makes was invisible until afterwards, which is the
 * wrong way round for the one step that writes into SharePoint. The names here
 * are the ones that will actually be used — the server sends them — with the
 * werf name following whatever is typed in the fields above, because the folder
 * is named after it.
 */
import type { DossierPlan, InkooporderAttachment, MailDetails } from '@/api/easykitInkooporder';

interface Props {
  mail: MailDetails | null;
  plan: DossierPlan | null;
  documents: InkooporderAttachment[];
  /** As it stands in the fields right now, corrections included. */
  werfName: string;
  lineCount: number;
}

export function InkooporderPlan({ mail, plan, documents, werfName, lineCount }: Props) {
  if (!plan && !mail) return null;

  const folderName = `${plan?.orderPlaceholder ?? 'S…'} ${werfName}`.trim();

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-4">
      {mail && (
        <section>
          <h2 className="text-sm font-bold text-td-navy">Uit de mail</h2>
          <dl className="mt-1 text-sm grid grid-cols-[6rem_1fr] gap-x-3 gap-y-0.5">
            <dt className="text-td-muted">Van</dt>
            <dd className="text-td-navy truncate" title={mail.from ?? ''}>{mail.from ?? '—'}</dd>
            <dt className="text-td-muted">Onderwerp</dt>
            <dd className="text-td-navy truncate" title={mail.subject}>{mail.subject || '—'}</dd>
            <dt className="text-td-muted">Verzonden</dt>
            <dd className="text-td-navy">{mail.sentAt ?? '—'}</dd>
          </dl>
          <p className="text-sm text-td-navy mt-2">
            {documents.length === 0
              ? 'Enkel de inkooporder — er zaten geen andere documenten bij.'
              : `De inkooporder, en ${documents.length} ${documents.length === 1 ? 'document' : 'documenten'} die erbij zaten:`}
          </p>
          {documents.length > 0 && (
            <ul className="mt-1 space-y-0.5">
              {documents.map((d) => (
                <li key={d.name} className="text-sm text-td-navy flex items-baseline gap-2">
                  <span className="truncate" title={d.name}>{d.name}</span>
                  <span className="text-xs text-td-muted shrink-0">{formatBytes(d.bytes)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {plan && (
        <section className="border-t border-gray-100 pt-3">
          <h2 className="text-sm font-bold text-td-navy">Wat er gebeurt als je importeert</h2>

          <p className="text-sm text-td-navy mt-2 font-semibold">In Odoo</p>
          <ul className="text-sm text-td-navy list-disc list-inside">
            <li>Eén concept-verkooporder op Easykit Group met {lineCount} {lineCount === 1 ? 'lijn' : 'lijnen'}. Niets wordt bevestigd.</li>
            <li>Een leveringscontact met het werfadres.</li>
            <li>De inkooporder als bijlage bij de order.</li>
          </ul>

          <p className="text-sm text-td-navy mt-3 font-semibold">In SharePoint</p>
          {plan.enabled ? (
            <>
              <p className="text-sm text-td-muted">
                Deze map, met het ordernummer dat Odoo straks geeft:
              </p>
              <p className="mt-0.5 text-sm font-mono text-td-navy break-all">
                {plan.root}/{plan.year}/{folderName}
              </p>

              <p className="text-sm text-td-muted mt-2">Met deze submappen:</p>
              <ul className="text-sm text-td-navy list-disc list-inside">
                {plan.subfolders.map((name) => <li key={name}>{name}</li>)}
              </ul>

              <p className="text-sm text-td-muted mt-2">En deze bestanden:</p>
              <ul className="text-sm text-td-navy list-disc list-inside">
                {plan.messageFileName && (
                  <li>
                    {plan.messageFileName}{' '}
                    <span className="text-xs text-td-muted">— de mail zelf</span>
                  </li>
                )}
                {plan.prefixedFiles.map((suffix) => (
                  <li key={suffix} className="break-all">{folderName} {suffix}</li>
                ))}
              </ul>
              <p className="text-xs text-td-muted mt-2">
                Bestaat er al iets met dezelfde naam, dan blijft dat staan — er wordt nooit iets
                overschreven.
              </p>
            </>
          ) : (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-1">
              Het dossier staat uit. Er wordt niets in SharePoint aangemaakt; de documenten uit de
              mail worden niet bewaard.
            </p>
          )}
        </section>
      )}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
